import { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Flame, TrendingUp, ExternalLink, Calendar, Wallet } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_META = {
  king:       { emoji: '\u{1F451}', label: 'King', years: '50+' },
  aristocrat: { emoji: '\u{1F3C6}', label: 'Aristocrat', years: '25+' },
  achiever:   { emoji: '\u{1F396}\uFE0F', label: 'Achiever', years: '10+' },
};

const FREQUENCY_PER_YEAR = {
  Weekly: 52,
  Monthly: 12,
  Quarterly: 4,
  'Semi-Annually': 2,
  Annually: 1,
};

const fmtMoney = (n, digits = 2) => {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return `$${Number(n).toFixed(digits)}`;
};

/**
 * StreakRaiseDetailModal
 * ----------------------
 * Slim modal that opens when a user clicks any Streaks Extended chip.
 * Focuses on the raise math itself — prior payment, new payment, %
 * bump, forward-looking annual income from the new rate — instead
 * of dumping the whole StockModal on them.
 *
 * If the user owns the stock (`shares` > 0), we surface their actual
 * personalized projected annual income at the new rate, plus the
 * delta vs the old rate. Pure UI on data already in the chip's
 * payload — no extra API calls except an optional fetch of
 * `dividend-growth` for the most recent payment date when present.
 */
export default function StreakRaiseDetailModal({
  open,
  onOpenChange,
  raise,           // The full streak item from /screener/streaks-this-week
  shares = null,   // If the user owns the stock — drives personalized math
  onOpenFullStock, // Escalation: open the full StockModal
}) {
  const [paymentsThisYear, setPaymentsThisYear] = useState(null);

  // Best-effort: check how many payments the company has already
  // made this year so we can communicate "X of Y payments this year
  // will be at the new rate" to set proper expectations.
  useEffect(() => {
    if (!open || !raise?.ticker) {
      setPaymentsThisYear(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/stocks/${raise.ticker}/dividend-growth`);
        if (cancelled || !res.data?.annual_data) return;
        const yr = new Date().getUTCFullYear();
        const thisYearRow = res.data.annual_data.find((r) => r.year === yr);
        if (thisYearRow && raise.recent_div_last_amount) {
          // Estimate payments-so-far by dividing this year's running
          // total by the new per-payment rate. Caps at the expected
          // payouts/year for the frequency.
          const ppy = FREQUENCY_PER_YEAR[raise.frequency] || 4;
          const est = Math.min(ppy, Math.round(thisYearRow.amount / raise.recent_div_last_amount));
          if (!cancelled) setPaymentsThisYear({ done: est, total: ppy });
        }
      } catch {
        // non-fatal
      }
    })();
    return () => { cancelled = true; };
  }, [open, raise]);

  if (!raise) return null;

  const meta = TIER_META[(raise.dividend_tier || '').toLowerCase()] || null;
  const last = Number(raise.recent_div_last_amount);
  const prev = Number(raise.recent_div_prev_amount);
  const delta = last - prev;
  const pct = Number(raise.recent_div_change_pct);
  const ppy = FREQUENCY_PER_YEAR[raise.frequency] || 4;

  // Forward-looking annual income at the NEW rate.
  const newAnnualPerShare = last * ppy;
  const oldAnnualPerShare = prev * ppy;

  // Personalized dollars (only if we have shares).
  const ownedNewAnnual = shares > 0 ? shares * newAnnualPerShare : null;
  const ownedOldAnnual = shares > 0 ? shares * oldAnnualPerShare : null;
  const ownedAnnualDelta = ownedNewAnnual != null ? ownedNewAnnual - ownedOldAnnual : null;

  // Display the next payment date prettily (slice "Jul 29, 2026" → "Jul 29")
  const niceNextDate = (() => {
    const d = raise.next_payment_date;
    if (!d || typeof d !== 'string') return null;
    const m = d.match(/^([A-Za-z]+\s+\d+),/);
    return m ? m[1] : d;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="streak-raise-modal">
        <DialogHeader className="pr-8">
          <DialogTitle className="flex items-center gap-x-2 gap-y-1 flex-wrap">
            <span className="inline-flex items-center gap-2">
              <Flame className="w-4 h-4 text-orange-500 shrink-0" />
              <span>Recent Raise · {raise.ticker}</span>
            </span>
            {meta && (
              <span className="text-xs font-normal text-muted-foreground">
                {meta.emoji} {meta.label} ({meta.years} yrs)
                {raise.dividend_streak_years
                  ? ` · ${raise.dividend_streak_years}y streak`
                  : ''}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {raise.name && (
            <div className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{raise.name}</span>
            </div>
          )}

          {/* The big raise stat — prior → new with green % bump */}
          <div className="rounded-lg border border-emerald-300/60 dark:border-emerald-700/40 bg-gradient-to-r from-emerald-50/70 to-amber-50/40 dark:from-emerald-950/20 dark:to-amber-950/15 px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
              Per-payment dividend
            </div>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-mono text-sm text-muted-foreground line-through">{fmtMoney(prev, 4)}</span>
              <span className="font-mono text-sm">→</span>
              <span className="font-mono text-2xl font-bold text-emerald-700 dark:text-emerald-300">{fmtMoney(last, 4)}</span>
              <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-xs font-bold ring-1 ring-emerald-500/30">
                <TrendingUp className="w-3 h-3" />
                +{pct.toFixed(2)}%
              </span>
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              +{fmtMoney(delta, 4)}/share per payment · declared {raise.recent_div_last_date}
            </div>
          </div>

          {/* Forward-looking annual run-rate */}
          <div className="rounded-lg border border-border/60 px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
              Forward annual run-rate
            </div>
            <div className="text-sm">
              If the next {ppy} {raise.frequency?.toLowerCase() || 'quarterly'}-payment
              {ppy === 1 ? '' : 's'} all match this new rate:
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
              <div className="rounded-md bg-muted/40 px-2 py-1.5">
                <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Per-share / yr</div>
                <div className="font-mono font-bold">
                  {fmtMoney(newAnnualPerShare)}{' '}
                  <span className="text-muted-foreground font-normal">
                    (was {fmtMoney(oldAnnualPerShare)})
                  </span>
                </div>
              </div>
              {paymentsThisYear && (
                <div className="rounded-md bg-muted/40 px-2 py-1.5">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Already paid this year</div>
                  <div className="font-mono font-bold">
                    {paymentsThisYear.done} of {paymentsThisYear.total}
                  </div>
                </div>
              )}
            </div>
            {niceNextDate && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <Calendar className="w-3 h-3" />
                Next payment: <span className="font-semibold">{fmtMoney(last)}</span> on <span className="font-semibold">{niceNextDate}</span>
              </div>
            )}
          </div>

          {/* Owned-only personalized projection */}
          {shares > 0 && ownedNewAnnual != null && (
            <div
              className="rounded-lg border border-emerald-400/60 dark:border-emerald-700/50 bg-emerald-50/60 dark:bg-emerald-950/20 px-4 py-3"
              data-testid="streak-raise-owned-projection"
            >
              <div className="flex items-center gap-1.5 mb-1.5">
                <Wallet className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 dark:text-emerald-300">
                  Your projection · {shares} shares
                </span>
              </div>
              <div className="text-2xl font-bold font-mono text-emerald-700 dark:text-emerald-300">
                {fmtMoney(ownedNewAnnual)}
                <span className="text-sm text-muted-foreground font-normal">/yr</span>
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                +{fmtMoney(ownedAnnualDelta)} more per year vs the old rate
                {' '}({fmtMoney(ownedOldAnnual)}/yr)
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={() => {
              onOpenChange?.(false);
              onOpenFullStock?.(raise.ticker);
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-foreground text-background text-xs font-semibold hover:opacity-90 transition-opacity"
            data-testid="streak-raise-open-stock-btn"
          >
            Open full stock detail
            <ExternalLink className="w-3 h-3" />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
